/**
 * What the Help Desk can see on the person's screen, and "Show me" (#1947
 * Phase 1).
 *
 * The Help Desk used to know only which page and record were open, never the
 * buttons on it, so its steps named buttons the person might not have. Now
 * each question carries the labels of the buttons and tabs that are visible
 * (text, or the tooltip / aria-label for icon buttons like the pencil and the
 * trash can), and an answer can end with `SHOW: <label>`, which the panel
 * turns into a "Show me" chip that lights that button up on the page.
 *
 * Only labels leave the browser, never field values. The Help Desk's own
 * panel is skipped.
 */

const CONTROL_SELECTOR = 'button, [role="button"], [role="tab"], a[href], summary, label[for]';
const MAX_LABELS = 80;
const MAX_LABEL_LEN = 48;

/** Text pieces joined with spaces: a tab drawn as <span>Contacts</span><span>4</span>
 *  reads "Contacts 4", not "Contacts4". */
function spacedText(el: Element): string {
    const parts: string[] = [];
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) parts.push(n.textContent ?? "");
    return parts.join(" ").replace(/\s+/g, " ").trim();
}

/** The words a person would use for this control: its text, else its
 *  tooltip. A trailing count is dropped ("Contacts 4" → "Contacts"). */
export function controlLabel(el: Element): string {
    const aria = el.getAttribute("aria-label")?.trim();
    const title = el.getAttribute("title")?.trim();
    const text = spacedText(el)
        .replace(/\s+\d+$/, "")
        .trim();
    const label = text.length >= 2 && !/^\d+$/.test(text) ? text : aria || title || "";
    return label.length > MAX_LABEL_LEN ? "" : label;
}

function insideHelpDesk(el: Element): boolean {
    return !!el.closest('aside[aria-label="Help Desk"], [data-help-desk-ignore]');
}

function isVisible(el: Element): boolean {
    const h = el as HTMLElement;
    // Closed toolbar panels and menus are inert and slid off screen.
    if (h.hidden || h.closest("[hidden], [aria-hidden='true'], [inert]")) return false;
    const r = h.getBoundingClientRect?.();
    if (!r || (r.width === 0 && r.height === 0)) return false;
    if (r.right <= 0 || r.left >= window.innerWidth) return false;
    const style = window.getComputedStyle(h);
    if (style.visibility === "hidden" || style.display === "none") return false;
    for (let n: Element | null = h; n && n !== document.body; n = n.parentElement) {
        if (window.getComputedStyle(n).opacity === "0") return false;
    }
    return true;
}

/** An open dialog first, then the page's main area, then everything else,
 *  so the toolbar's own buttons never crowd out the ones on the page. */
function controlsInOrder(root: ParentNode): Element[] {
    const scopes: ParentNode[] = [];
    if (root === document) {
        for (const d of Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]'))) scopes.push(d);
        const main = document.querySelector('main, [role="main"]');
        if (main) scopes.push(main);
    }
    scopes.push(root);
    const seen = new Set<Element>();
    const out: Element[] = [];
    for (const scope of scopes) {
        for (const el of Array.from(scope.querySelectorAll(CONTROL_SELECTOR))) {
            if (seen.has(el)) continue;
            seen.add(el);
            out.push(el);
        }
    }
    return out;
}

/** Labels of the visible buttons, tabs and links on the page (not the Help Desk). */
export function collectScreenControls(root: ParentNode = document): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const el of controlsInOrder(root)) {
        if (out.length >= MAX_LABELS) break;
        if (insideHelpDesk(el) || !isVisible(el)) continue;
        const label = controlLabel(el);
        const key = label.toLowerCase();
        if (label.length < 2 || seen.has(key)) continue;
        seen.add(key);
        out.push(label);
    }
    return out;
}

/** The control a "Show me" label points at: exact label first, then contains. */
export function findControl(label: string, root: ParentNode = document): HTMLElement | null {
    const want = label.trim().toLowerCase();
    if (!want) return null;
    const candidates = controlsInOrder(root).filter((el) => !insideHelpDesk(el) && isVisible(el)) as HTMLElement[];
    return (
        candidates.find((el) => controlLabel(el).toLowerCase() === want) ??
        candidates.find((el) => controlLabel(el).toLowerCase().includes(want)) ??
        null
    );
}

const RING_ID = "help-desk-show-me";

/** Remove the highlight, if one is up. */
export function clearShowMe(): void {
    document.getElementById(RING_ID)?.remove();
}

/**
 * Light up one control: scroll it into view, then draw a pulsing ring and a
 * "Press …" note over it for a few seconds. Returns false when the control
 * isn't on this screen, so the panel can say so in plain words.
 */
export function showMeOnPage(label: string, opts: { seconds?: number } = {}): boolean {
    const el = findControl(label);
    if (!el) return false;
    clearShowMe();
    el.scrollIntoView?.({ block: "center", inline: "nearest" });
    const draw = () => {
        const r = el.getBoundingClientRect();
        const wrap = document.createElement("div");
        wrap.id = RING_ID;
        wrap.setAttribute("role", "status");
        wrap.className = "hd-showme";
        wrap.style.cssText = `position:fixed;left:${r.left - 6}px;top:${r.top - 6}px;width:${r.width + 12}px;height:${r.height + 12}px;z-index:2147483000;pointer-events:none`;
        const ring = document.createElement("div");
        ring.className = "hd-showme-ring";
        const note = document.createElement("div");
        note.className = "hd-showme-note";
        note.textContent = `Press ${label}`;
        wrap.append(ring, note);
        document.body.appendChild(wrap);
    };
    // Let the scroll land before measuring.
    window.setTimeout(draw, 250);
    const ms = (opts.seconds ?? 7) * 1000;
    window.setTimeout(clearShowMe, ms + 250);
    const dismiss = () => {
        clearShowMe();
        window.removeEventListener("pointerdown", dismiss, true);
        window.removeEventListener("scroll", dismiss, true);
    };
    window.setTimeout(() => {
        window.addEventListener("pointerdown", dismiss, true);
        window.addEventListener("scroll", dismiss, true);
    }, 600);
    return true;
}

/* ------------------------------------------------------------------ */
/*  The words on the screen, and errors shown (#2178, audit step 1)     */
/* ------------------------------------------------------------------ */
/*
 * Button labels alone left the Help Desk blind. On 2 Nov Regina asked it twice
 * what was wrong while the red message saying exactly what was wrong ("Required
 * fields for this jurisdiction's form template are missing: McKinney device
 * type, …") sat on her screen; it asked her to read it out. Darcy asked where
 * the More button was while a mailbox list covered it.
 *
 * Now each question also carries the words the person can see (an open
 * dialog first, then the page's main area), and the error messages the app
 * showed them in the last few minutes. Typed field values are never read:
 * only text the page itself shows. The Help Desk's own panel is skipped.
 */

export const MAX_SCREEN_TEXT = 6000;
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEMPLATE", "SVG", "INPUT", "TEXTAREA", "SELECT", "OPTION"]);
const BLOCK_TAGS = new Set([
    "DIV",
    "P",
    "LI",
    "TR",
    "SECTION",
    "ARTICLE",
    "HEADER",
    "FOOTER",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "TABLE",
    "UL",
    "OL",
    "DL",
    "DT",
    "DD",
    "FORM",
    "FIELDSET",
    "LABEL",
    "BR",
    "HR",
    "ASIDE",
    "NAV",
    "MAIN",
]);

function hiddenElement(el: Element, cache: Map<Element, boolean>): boolean {
    const known = cache.get(el);
    if (known !== undefined) return known;
    let hidden = false;
    const h = el as HTMLElement;
    if (SKIP_TAGS.has(el.tagName.toUpperCase())) hidden = true;
    else if (h.hidden || el.getAttribute("aria-hidden") === "true" || el.hasAttribute("inert")) hidden = true;
    else if (insideHelpDesk(el)) hidden = true;
    else {
        const style = window.getComputedStyle(h);
        hidden = style.display === "none" || style.visibility === "hidden";
    }
    if (!hidden && el.parentElement && el.parentElement !== document.body) {
        hidden = hiddenElement(el.parentElement, cache);
    }
    cache.set(el, hidden);
    return hidden;
}

/** Visible text inside one element, with line breaks between blocks. */
export function visibleText(root: Element, max = MAX_SCREEN_TEXT): string {
    const cache = new Map<Element, boolean>();
    if (hiddenElement(root, cache)) return "";
    let out = "";
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    for (let n = walker.nextNode(); n && out.length < max; n = walker.nextNode()) {
        if (n.nodeType === Node.ELEMENT_NODE) {
            if (BLOCK_TAGS.has((n as Element).tagName.toUpperCase()) && !out.endsWith("\n")) out += "\n";
            continue;
        }
        const parent = n.parentElement;
        if (!parent || hiddenElement(parent, cache)) continue;
        const t = (n.textContent ?? "").replace(/\s+/g, " ");
        if (t.trim().length === 0) continue;
        out += out.endsWith("\n") || out.endsWith(" ") || out.length === 0 ? t.trimStart() : ` ${t.trimStart()}`;
    }
    return out
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .join("\n")
        .slice(0, max);
}

/** What the person can read right now: an open dialog first, then the
 *  page's main area (or the whole page when there is no main area). */
export function collectScreenText(max = MAX_SCREEN_TEXT): string {
    const parts: string[] = [];
    let left = max;
    const dialogs = Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]')).filter(
        (d) => !insideHelpDesk(d),
    );
    for (const d of dialogs) {
        if (left <= 0) break;
        const t = visibleText(d, left);
        if (t) {
            parts.push(`[Open window]\n${t}`);
            left -= t.length;
        }
    }
    const main = document.querySelector('main, [role="main"]') ?? document.body;
    if (left > 200) {
        const t = visibleText(main, left);
        if (t) parts.push(dialogs.length > 0 ? `[Page behind it]\n${t}` : t);
    }
    return parts.join("\n\n").slice(0, max);
}

/* Error messages the app showed: sonner's red toasts and role="alert" boxes.
   Kept in memory for 10 minutes, newest last, never more than 5. */
const ERROR_WINDOW_MS = 10 * 60 * 1000;
const MAX_ERRORS = 5;
let shownErrors: Array<{ text: string; at: number }> = [];
let observer: MutationObserver | null = null;

function noteError(text: string): void {
    const t = text.replace(/\s+/g, " ").trim().slice(0, 400);
    if (t.length < 3) return;
    const now = Date.now();
    shownErrors = shownErrors.filter((e) => now - e.at < ERROR_WINDOW_MS && e.text !== t);
    shownErrors.push({ text: t, at: now });
    if (shownErrors.length > MAX_ERRORS) shownErrors = shownErrors.slice(-MAX_ERRORS);
}

function scanForErrors(el: Element): void {
    const hits: Element[] = [];
    if (el.matches('[data-sonner-toast][data-type="error"], [role="alert"]')) hits.push(el);
    hits.push(...Array.from(el.querySelectorAll('[data-sonner-toast][data-type="error"], [role="alert"]')));
    for (const h of hits) {
        if (insideHelpDesk(h)) continue;
        noteError(spacedText(h));
    }
}

/** Start noting error messages (idempotent). Returns a stop function. */
export function startErrorWatch(): () => void {
    if (observer || typeof MutationObserver === "undefined") return () => {};
    observer = new MutationObserver((records) => {
        for (const r of records) {
            for (const n of Array.from(r.addedNodes)) {
                if (n.nodeType === Node.ELEMENT_NODE) scanForErrors(n as Element);
            }
            // A toast's words can arrive after its box does.
            if (r.type === "characterData" || r.type === "childList") {
                const host = (r.target as Node).parentElement?.closest?.(
                    '[data-sonner-toast][data-type="error"], [role="alert"]',
                );
                if (host && !insideHelpDesk(host)) noteError(spacedText(host));
            }
        }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => {
        observer?.disconnect();
        observer = null;
    };
}

/** Error messages shown in the last 10 minutes, oldest first. */
export function recentScreenErrors(now = Date.now()): string[] {
    shownErrors = shownErrors.filter((e) => now - e.at < ERROR_WINDOW_MS);
    return shownErrors.map((e) => e.text);
}

/** Test hook. */
export function clearScreenErrors(): void {
    shownErrors = [];
}
